import { DateTime } from "luxon";

export type TeenBirthdayWeekendDay = "Saturday" | "Sunday";

export interface TeenBirthdaySignal {
  isToday: boolean;
  /** Set on Monday when the birthday fell on the prior weekend. */
  weekendDay: TeenBirthdayWeekendDay | null;
}

// Consumes the mentor-safe `"MM-DD"` birthday (no year, no PHI).
export function resolveTeenBirthdaySignal(
  birthdayMd: string | null | undefined,
  now: DateTime = DateTime.now(),
): TeenBirthdaySignal {
  if (!birthdayMd) {
    return { isToday: false, weekendDay: null };
  }
  const match = /^(\d{2})-(\d{2})$/.exec(birthdayMd.trim());
  if (!match) {
    return { isToday: false, weekendDay: null };
  }
  const month = Number(match[1]);
  const day = Number(match[2]);
  // Validate against a leap year so 02-29 is accepted.
  if (!DateTime.fromObject({ year: 2000, month, day }).isValid) {
    return { isToday: false, weekendDay: null };
  }

  const sameDay = (d: DateTime) => d.month === month && d.day === day;

  if (sameDay(now)) {
    return { isToday: true, weekendDay: null };
  }

  // On Mondays the badge also fires for birthdays that fell over the weekend.
  if (now.weekday === 1) {
    const saturday = now.minus({ days: 2 });
    const sunday = now.minus({ days: 1 });
    if (sameDay(saturday)) {
      return { isToday: true, weekendDay: "Saturday" };
    }
    if (sameDay(sunday)) {
      return { isToday: true, weekendDay: "Sunday" };
    }
  }
  return { isToday: false, weekendDay: null };
}

export function isTeenBirthdayToday(
  birthdayMd: string | null | undefined,
  now: DateTime = DateTime.now(),
): boolean {
  return resolveTeenBirthdaySignal(birthdayMd, now).isToday;
}
